/**
 * THE validation-UX policy for every form in the app. Two rules, one home.
 *
 * RUN — the schema runs at mount, on every change, and on submit; never
 * conditionally. `field.handleChange()` therefore always leaves `meta.errors`,
 * `form.state.isValid` and `canSubmit` current, which is why no caller ever
 * calls `field.validate(...)` / `form.validateField(...)` again.
 *
 * DISPLAY — an error is shown once the field is COMMITTED: the user has both
 * EDITED and BLURRED it, or a submit attempt / server error wrote
 * `errorMap.onSubmit`. Discrete inputs (selects, pickers, switches, dates,
 * attachments) commit on change via `commitFieldValue`, which does both.
 */

type FormValidators = Record<string, unknown> | undefined;

/**
 * Normalize a form's `validators` so the schema runs on change AND on submit.
 * Pass either `onChange` or `onBlur` (both → `onChange` wins). Every other key
 * (`onMount`, the `*Async` variants, `onDynamic`, `onServer`,
 * `onChangeListenTo`) is forwarded untouched.
 *
 * Why exactly these two causes (verified against the pinned form-core):
 * - NO `onBlur` validator — form-core re-runs only the change-cause validator
 *   while typing and never clears a blur-cause `errorMap` entry, so a blur
 *   validator makes the message stick after the user fixes the value.
 * - NO injected `onMount` — mount-cause errors flip `canSubmit` false on a
 *   pristine form, which dead-locks the ~30 hand-rolled buttons gated on
 *   `canSubmit` (they must stay pressable so a submit attempt can reveal what
 *   is missing). Forms that need `isValid` honest while pristine pass their
 *   own `onMount`, accepting that consequence knowingly.
 * The one behavior this gives up: merely tabbing PAST an empty never-typed
 * field shows no error — its error appears on the first submit attempt. The
 * display gate below enforces that consistently, so the error cannot surface
 * later out of nowhere. Discrete inputs are unaffected (`commitFieldValue`
 * runs a change).
 */
export function applyValidationPolicy(
  validators: FormValidators,
): FormValidators {
  const schema = validators?.onChange ?? validators?.onBlur;
  if (!schema) return validators;
  return {
    ...validators,
    onChange: schema,
    onBlur: undefined,
    onSubmit: schema,
  };
}

export type FieldDisplayState = {
  /** `meta.isBlurred` — flips only on an explicit `field.handleBlur()`; cleared by `form.reset()`. */
  isBlurred: boolean;
  /** `meta.isDirty` — sticky from the first `handleChange`; cleared by `form.reset()`. */
  isDirty: boolean;
  /** `meta.errorMap.onSubmit != null` — a failed submit attempt, or `setFieldError`. */
  hasSubmitError: boolean;
};

/**
 * Whether a field that currently HAS an error is allowed to show it.
 *
 * `isBlurred` alone is NOT enough: a form-level schema writes its errors onto
 * EVERY field (form-core's `validateSync` walks all of `fieldMeta`), so a field
 * the user merely tabbed through — blurred, never edited, no error computed yet
 * because no validator runs on the blur cause — would light up later, at the
 * moment the user types in a DIFFERENT field. Requiring `isDirty` keeps the
 * reveal tied to the user's own interaction with THIS field; everything they
 * never touched is revealed together by the submit attempt.
 */
export function shouldDisplayFieldError(state: FieldDisplayState): boolean {
  return (state.isBlurred && state.isDirty) || state.hasSubmitError;
}
